import { ArrowRight, Bot, ChevronRight, Route, Sparkles, X } from "lucide-react";
import { Fragment } from "react";
import type { AgentHandoff, AgentHandoffSuggestion, RoutedAgent } from "@/types";

const ASSISTANT = "Assistant";

/** An agent's avatar: its color behind the agent icon; the assistant's look when there is no agent. */
export function AgentBadge({ agent, size = "md" }: Readonly<{ agent: RoutedAgent | null; size?: "sm" | "md" }>) {
  const box = size === "sm" ? "w-5 h-5 rounded" : "w-7 h-7 rounded-md";
  const icon = size === "sm" ? "w-3 h-3" : "w-4 h-4";
  if (!agent) {
    return (
      <span className={`${box} bg-brand-gradient-br flex items-center justify-center shrink-0`} title={ASSISTANT}>
        <Sparkles className={`${icon} text-white`} />
      </span>
    );
  }
  return (
    <span
      className={`${box} flex items-center justify-center shrink-0`}
      style={{ backgroundColor: agent.color || undefined }}
      title={agent.name}
    >
      <Bot className={`${icon} text-white`} />
    </span>
  );
}

/** The inline event where a chat moves to another agent (MHS-628): who, to whom, and always why. */
export function HandoffDivider({ handoff }: Readonly<{ handoff: AgentHandoff }>) {
  const from = handoff.fromAgent?.name ?? ASSISTANT;
  const to = handoff.toAgent.name;
  return (
    <div className="flex items-center gap-3 py-1" role="note" aria-label={`${from} to ${to}: ${handoff.reason}`}>
      <div className="h-px flex-1 bg-border-secondary" />
      <div className="flex flex-col items-center gap-1 max-w-xl text-center">
        <div className="flex items-center gap-2">
          <AgentBadge agent={handoff.fromAgent} />
          <ArrowRight className="w-4 h-4 text-content-tertiary" />
          <AgentBadge agent={handoff.toAgent} />
        </div>
        <span className="text-sm text-content-secondary">
          {handoff.initiator === "llm" ? (
            <>
              <span className="font-medium text-content-primary">{from}</span> handed off to{" "}
              <span className="font-medium text-content-primary">{to}</span>
            </>
          ) : (
            <>
              You switched from <span className="font-medium text-content-primary">{from}</span> to{" "}
              <span className="font-medium text-content-primary">{to}</span>
            </>
          )}
        </span>
        <span className="text-xs text-content-tertiary">{handoff.reason}</span>
        <span className="text-xs text-content-quaternary">The conversation continues with {to}</span>
      </div>
      <div className="h-px flex-1 bg-border-secondary" />
    </div>
  );
}

/** The chat's agents in order, first to current, with each step's reason in the tooltip. */
export function HandoffChain({ handoffs }: Readonly<{ handoffs: AgentHandoff[] }>) {
  if (handoffs.length === 0) return null;
  const agents = [handoffs[0].fromAgent, ...handoffs.map((h) => h.toAgent)];
  const steps = handoffs.map((h) => `${h.fromAgent?.name ?? ASSISTANT} → ${h.toAgent.name}: ${h.reason}`).join("\n");
  return (
    <fieldset
      className="hidden md:flex items-center gap-1 min-w-0"
      title={steps}
      aria-label={`Agent handoffs: ${steps}`}
    >
      {agents.map((agent, index) => (
        <Fragment key={`${agent?.uuid ?? "assistant"}-${index}`}>
          {index > 0 && <ChevronRight className="w-3 h-3 text-content-quaternary" />}
          <AgentBadge agent={agent} size="sm" />
        </Fragment>
      ))}
    </fieldset>
  );
}

/** The router's mid-chat suggestion (MHS-628): it only suggests; nothing changes unless the user switches. */
export function HandoffSuggestionRow({
  suggestion,
  onSwitch,
  onDismiss,
}: Readonly<{ suggestion: AgentHandoffSuggestion; onSwitch: () => void; onDismiss: () => void }>) {
  return (
    <div className="flex gap-4" aria-live="polite">
      <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center shrink-0">
        <Route className="w-4 h-4 text-brand-primary" />
      </div>
      <div className="flex-1 min-w-0 flex items-start gap-2 rounded-lg border border-border-secondary bg-surface-secondary/50 px-3 py-2 text-sm">
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <AgentBadge agent={suggestion.agent} size="sm" />
            <span className="text-content-secondary">
              <span className="font-medium text-content-primary">{suggestion.agent.name}</span> may be better for this
            </span>
            <button type="button" onClick={onSwitch} className="text-brand-primary underline-offset-2 hover:underline">
              Switch to {suggestion.agent.name}
            </button>
          </div>
          <span className="text-xs text-content-tertiary">{suggestion.reason}</span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss suggestion"
          className="p-1 rounded hover:bg-interactive-hover text-content-tertiary"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
